import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mail, Linkedin, MapPin, Globe, MessageSquare, X } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface TeamMember {
  name: string;
  role: string;
  avatar: string;
  linkedin: string;
  email: string;
}

const EmailModal = ({ member, isOpen, onClose }: { member: TeamMember | null; isOpen: boolean; onClose: () => void }) => {
  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl">Contact {member.name}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-lg mb-4">You can reach out via email:</p>
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <Mail className="w-8 h-8 text-blue-500" />
            <a 
              href={`mailto:${member.email}`}
              className="text-xl font-medium text-blue-600 hover:underline break-all"
            >
              {member.email}
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Contact() {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const teamMembers: TeamMember[] = [
    {
      name: "Sworaj Nepal",
      role: "Co-Founder & Developer",
      avatar: "https://avatars.githubusercontent.com/u/67495678?v=4",
      linkedin: "https://www.linkedin.com/in/sworaj-nepal-b9a821251/",
      email: "sergiosworaj10@gmail.com"
    },
    {
      name: "Sushant Raut",
      role: "Co-Founder & Developer",
      avatar: "https://avatars.githubusercontent.com/u/130800473?v=4",
      linkedin: "https://www.linkedin.com/in/sushant-raut173/",
      email: "sushantraut178@gmail.com"
    }
  ];
  const handleEmailClick = (member: TeamMember) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <EmailModal 
        member={selectedMember} 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
      {/* Hero Section */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-3">
              Contact Us
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              We'd love to hear from you
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Team Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-semibold text-gray-900 mb-3">
            Meet Our Team
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            The minds behind MadeForTeams
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto mb-28">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <div className="h-full flex flex-col items-center text-center">
                <div className="relative mb-8">
                  <Avatar className="w-48 h-48 border-8 border-white shadow-lg group-hover:opacity-90 transition-opacity">
                    <AvatarImage src={member.avatar} alt={member.name} className="object-cover" />
                    <AvatarFallback className="bg-gray-100">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <h3 className="text-2xl font-medium text-gray-900 mb-1">{member.name}</h3>
                <p className="text-lg text-gray-500 mb-4">{member.role}</p>
                <div className="flex space-x-3">
                  <button 
                    onClick={() => handleEmailClick(member)}
                    className="p-3 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    aria-label={`Email ${member.name}`}
                  >
                    <Mail className="w-6 h-6" />
                  </button>
                  <a 
                    href={member.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-3 rounded-full bg-gray-100 text-blue-600 hover:bg-blue-50 transition-colors"
                    aria-label={`${member.name}'s LinkedIn`}
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <Separator className="my-16" />

        {/* About Section */}
        <div className="max-w-4xl mx-auto mb-28">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">
              About MadeForTeams
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Reimagining collaboration for modern teams
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-medium text-gray-900 mb-6">Our Vision</h3>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                At MadeForTeams, we believe in the power of seamless collaboration. Our platform brings teams together 
                with intuitive tools that make brainstorming, planning, and creating together feel natural and productive.
              </p>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We're committed to building tools that adapt to your workflow, not the other way around. Our mission is 
                to eliminate the friction from remote collaboration, no matter where your team is located.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-500">
                    <MessageSquare className="h-full w-full" />
                  </div>
                  <p className="ml-3 text-xl text-gray-600">
                    Special thanks to our mentor <span className="font-medium text-gray-900">Shyam Kaji Khatri</span> for his guidance and support.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-500">
                    <Globe className="h-full w-full" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-xl font-medium text-gray-900">Location</h4>
                    <p className="mt-2 text-lg text-gray-600">Kathmandu, Nepal</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-500">
                    <Mail className="h-full w-full" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-xl font-medium text-gray-900">Email</h4>
                    <p className="mt-2">
                      <a 
                        href="mailto:contact@madeforteams.com" 
                        className="text-lg text-blue-600 hover:underline"
                      >
                        fashnep@gmail.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} MadeForTeams. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};